import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { Button, Icon, Skeleton, WidgetCard } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { patientModel } from '@/entities/patient';

import styles from './ClinicalCaseDescription.module.scss';

type ClinicalCaseDescriptionProps = {
  patientID: string;
  className?: string;
  // 'plain' drops the card chrome and moves editing to a footer button, for
  // embedding into another card
  layout?: 'card' | 'plain';
  testID?: string;
};

export const ClinicalCaseDescription: FC<ClinicalCaseDescriptionProps> = (
  props,
) => {
  const { className, layout = 'card', testID, patientID } = props;

  const dispatch = useAppDispatch();
  const { isMobile } = useMedia();

  const patientLoading = useAppSelector(patientModel.selectors.selectLoading);
  const { CanEditClinicalCaseDescription, CanViewClinicalCaseDescription } =
    useAppSelector(
      patientModel.selectors.selectPatientYourPermissions(patientID),
    );

  const clinicalCaseDescription = useAppSelector(
    patientModel.selectors.selectClinicalCaseDescription,
  );

  if (patientLoading === 'pending') {
    return <Skeleton width="100%" height="148px" borderRadius="16px" />;
  }

  if (!CanViewClinicalCaseDescription) {
    return null;
  }

  const isPlain = layout === 'plain';

  const openDescriptionEditorModalHandler = () => {
    if (CanEditClinicalCaseDescription) {
      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.ClinicalCaseDescriptionEditorModal,
          data: {
            patientID,
          },
        }),
      );
    }
  };

  const content = (
    <>
      <div className={cn(styles.title, isMobile ? 'h4m' : 'h4')}>
        <FormattedMessage
          id="patientInfo.clinicalCaseDescription"
          defaultMessage="Clinical case description"
        />

        {CanEditClinicalCaseDescription && !isPlain && (
          <Icon
            name="pen"
            size={isMobile ? 24 : 34}
            className={styles.editIcon}
            onClick={openDescriptionEditorModalHandler}
          />
        )}
      </div>

      {clinicalCaseDescription ? (
        <div
          className={cn(styles.description, isMobile ? 'p3' : 'p2')}
          data-ph-mask
        >
          {clinicalCaseDescription}
        </div>
      ) : (
        <Icon
          name="doc"
          size={48}
          className={cn(
            styles.addIcon,
            !CanEditClinicalCaseDescription && styles.cursorAuto,
          )}
          onClick={openDescriptionEditorModalHandler}
        />
      )}

      {CanEditClinicalCaseDescription && isPlain && (
        <div className={styles.actions}>
          <Button
            variant="tertiary"
            size="small"
            icon="pen"
            onClick={openDescriptionEditorModalHandler}
          >
            <FormattedMessage id="global.edit" defaultMessage="Edit" />
          </Button>
        </div>
      )}
    </>
  );

  return isPlain ? (
    <div
      className={cn(styles.container, styles.plain, className)}
      data-testid={testID}
      data-component="clinical-case-description"
    >
      {content}
    </div>
  ) : (
    <WidgetCard
      className={cn(styles.container, className)}
      data-testid={testID}
      data-component="clinical-case-description"
    >
      {content}
    </WidgetCard>
  );
};
